import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  List,
  ListItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Search } from '@material-ui/icons';
import NavAppbar from '../../nav-top/NavAppbar';
import { getUserState } from '../../../features/user/selectors';
import { addSnackbar } from '../../../features/snackbar/actions';
import { getProjectDisplayName } from '../../../scripts/project-name-utils';
import { CENTERLINE_AI_ENABLED } from '../centerline-ai-access';
import {
  getQueryToolAdminSubscribers,
  getQueryToolSubscriberProjects,
  IQueryToolAdminProject,
  IQueryToolAdminSubscriber,
  QueryToolSetting,
  setQueryToolEntitlement,
  setQueryToolProjectSettings,
  setQueryToolSwitch,
} from '../../../models/api/query-tool';
import {
  blockedReason,
  countByFilter,
  FILTER_LABELS,
  filterProjects,
  planBulkChange,
  ProjectFilter,
  searchProjects,
  searchSubscribers,
  SETTING_LABELS,
  SETTINGS,
  sortByName,
  SubscriberFilter,
  subscriberSummary,
} from './centerline-ai-admin-utils';

/**
 * CENTERLINE AI for site admins: which subscribers may use it, and each of their projects' two
 * settings, Ingestion and Chat, each on or off. Routed at /main/site-admin/centerline-ai in builds with
 * REACT_APP_ENABLE_CENTERLINE_AI; anyone but a site admin is sent back to /main. The backend checks
 * `isSiteAdmin` on every route it calls (query-tool-admin-service.ts).
 */

const LIVE = '#1B7F3B';
const MUTED = '#6B7280';
const LINE = '#E5E7EB';
const SELECTED_BG = '#E3ECFF';

const FILTERS: ProjectFilter[] = ['all', 'ingest', 'chat', 'off'];

/** What each setting does, for the column headers' tooltips. */
const SETTING_HINTS: Record<QueryToolSetting, string> = {
  ingest:
    "The Query Tool ingests the project's files and emails: those it hasn't yet when switched on, then new ones within about a minute.",
  chat: 'The project is available in the CENTERLINE AI chat.',
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // The theme's base text (body2) is small capitals; this page reads as plain text.
    page: {
      flexGrow: 1,
      minWidth: 0,
      fontFamily: 'Roboto, Arial, sans-serif',
      fontSize: 14,
      fontWeight: 400,
      textTransform: 'none',
      color: theme.palette.text.primary,
      '& .MuiTableBody-root .MuiTableCell-root, & .MuiInputBase-root, & .MuiTablePagination-caption': {
        fontFamily: 'inherit',
        fontSize: 14,
        textTransform: 'none',
      },
    },
    // Doubled (&&) to win over the global stylesheets' heading sizes.
    heading: { '&&': { fontSize: 24, fontWeight: 500, margin: theme.spacing(1, 0, 0.5) } },
    sub: { color: MUTED, margin: 0 },
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: theme.spacing(2),
      flexWrap: 'wrap',
      marginBottom: theme.spacing(2.5),
    },
    globalSwitch: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1, 1, 2),
      textAlign: 'right',
    },
    globalOff: {
      margin: theme.spacing(0, 0, 2.5),
      padding: theme.spacing(1.5, 2),
      borderRadius: 4,
      background: '#FDECEA',
      color: '#8B1A1A',
      fontSize: 14,
    },
    cardTitle: { '&&': { fontSize: 16, fontWeight: 500, margin: 0 } },
    detailTitle: { '&&': { fontSize: 20, fontWeight: 500, margin: theme.spacing(0, 0, 0.75) } },
    accessLabel: { fontWeight: 500 },
    grid: {
      display: 'grid',
      gridTemplateColumns: '320px minmax(0, 1fr)',
      gap: theme.spacing(2.5),
      alignItems: 'start',
      [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' },
    },
    cardHeader: {
      padding: theme.spacing(1.75, 2),
      borderBottom: `1px solid ${LINE}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    count: { color: MUTED, fontSize: 12 },
    search: { margin: theme.spacing(1.5, 2, 1) },
    filterRow: { display: 'flex', gap: theme.spacing(0.75), padding: theme.spacing(0, 2, 1.25) },
    filterChip: {
      border: '1px solid #CBD5E1',
      borderRadius: 14,
      padding: '2px 10px',
      fontSize: 12,
      color: MUTED,
      background: 'none',
      cursor: 'pointer',
      fontFamily: 'inherit',
    },
    filterChipOn: {
      background: SELECTED_BG,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
    subscriberList: { maxHeight: 'calc(100vh - 300px)', minHeight: 240, overflowY: 'auto' },
    subscriberItem: {
      borderLeft: '3px solid transparent',
      display: 'flex',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
    },
    subscriberItemSelected: {
      background: `${SELECTED_BG} !important`,
      borderLeftColor: theme.palette.primary.main,
    },
    subscriberName: { fontWeight: 500, fontSize: 14 },
    subscriberMeta: { color: MUTED, fontSize: 12, marginTop: 2 },
    access: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      whiteSpace: 'nowrap',
    },
    dot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },
    detailHeader: {
      padding: theme.spacing(2.25, 2.5),
      borderBottom: `1px solid ${LINE}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
      flexWrap: 'wrap',
    },
    stats: { display: 'flex', gap: theme.spacing(2), color: MUTED, fontSize: 13 },
    statValue: { color: theme.palette.text.primary, fontWeight: 500 },
    accessSwitch: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      textAlign: 'right',
    },
    accessHint: { color: MUTED, fontSize: 12 },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.75, 2.5),
      flexWrap: 'wrap',
    },
    projectSearch: { width: 280, maxWidth: '100%' },
    filterButton: { textTransform: 'none', fontWeight: 400 },
    filterCount: { opacity: 0.75, marginLeft: 4 },
    showInactive: { marginLeft: 'auto' },
    showInactiveLabel: { color: MUTED, fontSize: 13, fontFamily: 'inherit', textTransform: 'none' },
    notice: {
      margin: theme.spacing(0, 2.5, 1.25),
      padding: theme.spacing(1.25, 1.75),
      borderRadius: 4,
      background: '#FFF8EC',
      color: '#8A5300',
      fontSize: 13,
    },
    bulkBar: {
      margin: theme.spacing(0, 2.5, 1.25),
      padding: theme.spacing(1, 1.75),
      borderRadius: 4,
      background: SELECTED_BG,
      color: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      fontWeight: 500,
      flexWrap: 'wrap',
    },
    bulkButton: { background: '#fff', textTransform: 'none' },
    headCell: {
      fontFamily: 'inherit',
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: MUTED,
      fontWeight: 500,
    },
    rowSelected: { '&&': { backgroundColor: '#F5F8FF' } },
    // The dialog renders outside the page, so it needs the page's text style itself.
    dialogText: {
      fontFamily: 'Roboto, Arial, sans-serif',
      fontSize: 14,
      textTransform: 'none',
      color: MUTED,
    },
    projectName: { fontWeight: 500 },
    badge: {
      fontSize: 11,
      borderRadius: 3,
      padding: '1px 6px',
      background: '#F3F4F6',
      color: MUTED,
      marginLeft: 8,
    },
    bulkLabel: { marginLeft: theme.spacing(1) },
    headHint: { cursor: 'help', borderBottom: `1px dotted ${MUTED}` },
    settingCell: { width: 110, whiteSpace: 'nowrap' },
    empty: { padding: theme.spacing(6, 2), textAlign: 'center', color: MUTED },
  }),
);

export default function CenterlineAiAdminPage() {
  const user = useSelector(getUserState);
  // Until the signed-in user has loaded, nothing; then site admins only.
  if (!user?.id) return null;
  if (!CENTERLINE_AI_ENABLED || !user.isSiteAdmin) return <Redirect to="/main" />;
  return <CenterlineAiAdmin />;
}

function CenterlineAiAdmin() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const notify = useCallback(
    (message: string) => dispatch(addSnackbar({ id: Date.now(), message, severity: 'success' })),
    [dispatch],
  );

  const [subscribers, setSubscribers] = useState<IQueryToolAdminSubscriber[] | null>(null);
  const [subscriberSearch, setSubscriberSearch] = useState('');
  const [subscriberFilter, setSubscriberFilter] = useState<SubscriberFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [entitled, setEntitled] = useState(false);
  const [projects, setProjects] = useState<IQueryToolAdminProject[] | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [filter, setFilter] = useState<ProjectFilter>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [accessBusy, setAccessBusy] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  // Bulk Ingestion on starts ingesting every file of the projects: confirmed first.
  const [confirmBulkIngest, setConfirmBulkIngest] = useState(false);
  // CENTERLINE AI's global switch, and the change waiting for confirmation.
  const [switchedOn, setSwitchedOn] = useState<boolean | null>(null);
  const [confirmSwitch, setConfirmSwitch] = useState<boolean | null>(null);
  const [switchBusy, setSwitchBusy] = useState(false);

  useEffect(() => {
    getQueryToolAdminSubscribers()
      .then(({ enabled, subscribers: unsorted }) => {
        setSwitchedOn(enabled);
        const list = sortByName(unsorted);
        setSubscribers(list);
        // Start on a subscriber with access, if any.
        setSelectedId(
          (current) => current ?? (list.find((s) => s.entitled) ?? list[0])?.id ?? null,
        );
      })
      .catch(() => setSubscribers([]));
  }, []);

  const loadProjects = useCallback(async (subscriberId: string) => {
    const result = await getQueryToolSubscriberProjects(subscriberId);
    setEntitled(result.entitled);
    setProjects(sortByName(result.projects));
    return result;
  }, []);

  useEffect(() => {
    setProjects(null);
    setProjectSearch('');
    setFilter('all');
    setPage(0);
    setChecked(new Set());
    if (selectedId) loadProjects(selectedId).catch(() => setProjects([]));
  }, [selectedId, loadProjects]);

  // Keep the selected subscriber's summary in step with its projects.
  useEffect(() => {
    if (!selectedId || !projects) return;
    const counted = projects.filter((p) => !p.isHidden);
    setSubscribers((list) =>
      list
        ? list.map((s) =>
            s.id === selectedId
              ? {
                  ...s,
                  entitled,
                  projects: counted.length,
                  ingesting: counted.filter((p) => p.ingest).length,
                  chat: counted.filter((p) => p.chat).length,
                }
              : s,
          )
        : list,
    );
  }, [projects, entitled, selectedId]);

  const selected = subscribers?.find((s) => s.id === selectedId) ?? null;
  const shownSubscribers = useMemo(
    () => searchSubscribers(subscribers ?? [], subscriberSearch, subscriberFilter),
    [subscribers, subscriberSearch, subscriberFilter],
  );
  const withAccess = (subscribers ?? []).filter((s) => s.entitled).length;

  const searched = useMemo(() => searchProjects(projects ?? [], projectSearch, showInactive), [
    projects,
    projectSearch,
    showInactive,
  ]);
  const counts = useMemo(() => countByFilter(searched), [searched]);
  const filtered = useMemo(() => filterProjects(searched, filter), [searched, filter]);
  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const allCounts = useMemo(() => countByFilter((projects ?? []).filter((p) => !p.isHidden)), [
    projects,
  ]);
  const checkedProjects = (projects ?? []).filter((p) => checked.has(p.id));

  useEffect(() => setPage(0), [projectSearch, filter, showInactive, rowsPerPage]);

  const setRows = (ids: string[], setting: QueryToolSetting, on: boolean) =>
    setProjects((list) =>
      list ? list.map((p) => (ids.includes(p.id) ? { ...p, [setting]: on } : p)) : list,
    );

  const changeSetting = async (
    project: IQueryToolAdminProject,
    setting: QueryToolSetting,
    on: boolean,
  ) => {
    if (project[setting] === on || blockedReason(project, setting, on, entitled)) return;
    setBusy((b) => new Set(b).add(project.id));
    try {
      await setQueryToolProjectSettings(project.id, { [setting]: on });
      setRows([project.id], setting, on);
      notify(`${getProjectDisplayName(project)}: ${SETTING_LABELS[setting]} ${on ? 'on' : 'off'}.`);
    } catch {
      // The API client has already shown the server's reason.
    } finally {
      setBusy((b) => {
        const next = new Set(b);
        next.delete(project.id);
        return next;
      });
    }
  };

  const changeChecked = async (setting: QueryToolSetting, on: boolean) => {
    setConfirmBulkIngest(false);
    const plan = planBulkChange(checkedProjects, setting, on, entitled);
    setBusy(new Set(plan.change.map((p) => p.id)));
    const done: string[] = [];
    // One at a time: each change is checked and recorded by the backend on its own.
    // eslint-disable-next-line no-restricted-syntax
    for (const project of plan.change) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await setQueryToolProjectSettings(project.id, { [setting]: on });
        done.push(project.id);
      } catch {
        // Shown by the API client; the rest carry on.
      }
    }
    setRows(done, setting, on);
    setBusy(new Set());
    setChecked(new Set());
    const onOff = on ? 'on' : 'off';
    const extra = [
      plan.unchanged ? `${plan.unchanged} already ${onOff}` : '',
      plan.skipped ? `${plan.skipped} skipped` : '',
    ]
      .filter(Boolean)
      .join(', ');
    notify(
      `${SETTING_LABELS[setting]} ${onOff} for ${done.length} project${
        done.length === 1 ? '' : 's'
      }${extra ? ` (${extra})` : ''}.`,
    );
  };

  const changeSwitch = async (enabled: boolean) => {
    setConfirmSwitch(null);
    setSwitchBusy(true);
    try {
      const result = await setQueryToolSwitch(enabled);
      setSwitchedOn(result.enabled);
      notify(result.enabled ? 'CENTERLINE AI is back on.' : 'CENTERLINE AI is off everywhere.');
    } catch {
      // Shown by the API client.
    } finally {
      setSwitchBusy(false);
    }
  };

  const changeAccess = async (enabled: boolean) => {
    if (!selected) return;
    setAccessBusy(true);
    setConfirmOff(false);
    try {
      const { projectsSwitchedOff } = await setQueryToolEntitlement(selected.id, enabled);
      await loadProjects(selected.id);
      notify(
        enabled
          ? `CENTERLINE AI access turned on for ${selected.name}.`
          : `CENTERLINE AI access turned off for ${selected.name}; ${projectsSwitchedOff} project${
              projectsSwitchedOff === 1 ? '' : 's'
            } switched off.`,
      );
    } catch {
      // Shown by the API client.
    } finally {
      setAccessBusy(false);
    }
  };

  const allOnPageChecked = pageRows.length > 0 && pageRows.every((p) => checked.has(p.id));
  const toggleAllOnPage = () =>
    setChecked((c) => {
      const next = new Set(c);
      pageRows.forEach((p) => (allOnPageChecked ? next.delete(p.id) : next.add(p.id)));
      return next;
    });
  const toggleOne = (id: string) =>
    setChecked((c) => {
      const next = new Set(c);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onCount = allCounts.all - allCounts.off;
  const bulkIngest = planBulkChange(checkedProjects, 'ingest', true, entitled).change.length;

  return (
    <div className={classes.page}>
      <NavAppbar />
      <div className={classes.pageHeader}>
        <div>
          <h1 className={classes.heading}>CENTERLINE AI</h1>
          <div className={classes.sub}>
            Which subscribers can use CENTERLINE AI and, for each of their projects, whether the
            Query Tool ingests its files and whether it is in the chat. Site admins only.
          </div>
        </div>
        <Paper className={classes.globalSwitch}>
          <div>
            <div className={classes.accessLabel}>
              {switchedOn === false ? 'CENTERLINE AI is off everywhere' : 'CENTERLINE AI is on'}
            </div>
            <div className={classes.accessHint}>For everyone, site admins included</div>
          </div>
          <Switch
            color="primary"
            checked={!!switchedOn}
            disabled={switchedOn === null || switchBusy}
            onChange={(e, on) => setConfirmSwitch(on)}
            inputProps={{ 'aria-label': 'CENTERLINE AI, everywhere' }}
          />
        </Paper>
      </div>
      {switchedOn === false ? (
        <div className={classes.globalOff} role="status">
          <strong>CENTERLINE AI is off everywhere.</strong> Nobody can use it, site admins included,
          and the Query Tool reads nothing. The access and project settings below are kept, and take
          effect again when it is turned back on.
        </div>
      ) : null}

      <div className={classes.grid}>
        {/* Subscribers */}
        <Paper>
          <div className={classes.cardHeader}>
            <h2 className={classes.cardTitle}>Subscribers</h2>
            <span className={classes.count}>{subscribers?.length ?? ''}</span>
          </div>
          <TextField
            className={classes.search}
            style={{ width: 'calc(100% - 32px)' }}
            variant="outlined"
            size="small"
            placeholder="Search subscribers"
            value={subscriberSearch}
            onChange={(e) => setSubscriberSearch(e.target.value)}
            inputProps={{ 'aria-label': 'Search subscribers' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <div className={classes.filterRow}>
            {(['all', 'access'] as SubscriberFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                className={`${classes.filterChip} ${
                  subscriberFilter === f ? classes.filterChipOn : ''
                }`}
                onClick={() => setSubscriberFilter(f)}
              >
                {f === 'all' ? 'All' : `With access · ${withAccess}`}
              </button>
            ))}
          </div>
          {subscribers === null ? (
            <div className={classes.empty}>
              <CircularProgress size={24} />
            </div>
          ) : (
            <List disablePadding className={classes.subscriberList} aria-label="Subscribers">
              {shownSubscribers.map((s) => (
                <ListItem
                  key={s.id}
                  button
                  selected={s.id === selectedId}
                  classes={{ selected: classes.subscriberItemSelected }}
                  className={classes.subscriberItem}
                  onClick={() => setSelectedId(s.id)}
                >
                  <div>
                    <div className={classes.subscriberName}>{s.name}</div>
                    <div className={classes.subscriberMeta}>{subscriberSummary(s)}</div>
                  </div>
                  <span className={classes.access} style={{ color: s.entitled ? LIVE : MUTED }}>
                    <span
                      className={classes.dot}
                      style={{ background: s.entitled ? LIVE : '#CBD5E1' }}
                    />
                    {s.entitled ? 'Access' : 'No access'}
                  </span>
                </ListItem>
              ))}
              {!shownSubscribers.length ? (
                <div className={classes.empty}>No subscribers match.</div>
              ) : null}
            </List>
          )}
        </Paper>

        {/* The selected subscriber */}
        <Paper>
          {!selected ? (
            <div className={classes.empty}>Select a subscriber.</div>
          ) : (
            <>
              <div className={classes.detailHeader}>
                <div>
                  <h2 className={classes.detailTitle}>{selected.name}</h2>
                  <div className={classes.stats}>
                    <span>
                      <span className={classes.statValue}>{allCounts.ingest}</span> ingesting
                    </span>
                    <span>
                      <span className={classes.statValue}>{allCounts.chat}</span> in the chat
                    </span>
                    <span>
                      <span className={classes.statValue}>{allCounts.off}</span> off
                    </span>
                    <span>
                      {allCounts.all} {allCounts.all === 1 ? 'project' : 'projects'}
                    </span>
                  </div>
                </div>
                <div className={classes.accessSwitch}>
                  <div>
                    <div className={classes.accessLabel}>CENTERLINE AI access</div>
                    <div className={classes.accessHint}>
                      Turning it off switches every project off
                    </div>
                  </div>
                  <Switch
                    color="primary"
                    checked={entitled}
                    disabled={accessBusy || projects === null}
                    onChange={(e, on) => (on ? changeAccess(true) : setConfirmOff(true))}
                    inputProps={{ 'aria-label': 'CENTERLINE AI access' }}
                  />
                </div>
              </div>

              <div className={classes.toolbar}>
                <TextField
                  className={classes.projectSearch}
                  variant="outlined"
                  size="small"
                  placeholder="Search projects"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  inputProps={{ 'aria-label': 'Search projects' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <ButtonGroup size="small" color="primary" aria-label="Filter projects by setting">
                  {FILTERS.map((f) => (
                    <Button
                      key={f}
                      className={classes.filterButton}
                      variant={filter === f ? 'contained' : 'outlined'}
                      onClick={() => setFilter(f)}
                    >
                      {FILTER_LABELS[f]}
                      <span className={classes.filterCount}>{counts[f]}</span>
                    </Button>
                  ))}
                </ButtonGroup>
                <FormControlLabel
                  className={classes.showInactive}
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={showInactive}
                      onChange={(e, on) => setShowInactive(on)}
                    />
                  }
                  label="Show archived and deleted"
                  classes={{ label: classes.showInactiveLabel }}
                />
              </div>

              {!entitled && projects !== null ? (
                <div className={classes.notice}>
                  This subscriber doesn&apos;t have CENTERLINE AI access. Turn it on to switch on
                  Ingestion or Chat for its projects.
                </div>
              ) : null}

              {checked.size ? (
                <div className={classes.bulkBar}>
                  {checked.size} selected —
                  {SETTINGS.map((setting) => (
                    <React.Fragment key={setting}>
                      <span className={classes.bulkLabel}>{SETTING_LABELS[setting]}</span>
                      {[true, false].map((on) => (
                        <Button
                          key={String(on)}
                          size="small"
                          variant="outlined"
                          color="primary"
                          className={classes.bulkButton}
                          disabled={busy.size > 0}
                          onClick={() =>
                            setting === 'ingest' && on && bulkIngest
                              ? setConfirmBulkIngest(true)
                              : changeChecked(setting, on)
                          }
                        >
                          {on ? 'On' : 'Off'}
                        </Button>
                      ))}
                    </React.Fragment>
                  ))}
                  <Button
                    size="small"
                    color="primary"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => setChecked(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              ) : null}

              {projects === null ? (
                <div className={classes.empty}>
                  <CircularProgress size={24} />
                </div>
              ) : (
                <>
                  <Table size="small" aria-label="Projects">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={allOnPageChecked}
                            indeterminate={
                              !allOnPageChecked && pageRows.some((p) => checked.has(p.id))
                            }
                            onChange={toggleAllOnPage}
                            inputProps={{ 'aria-label': 'Select all on this page' }}
                          />
                        </TableCell>
                        <TableCell className={classes.headCell}>Project</TableCell>
                        {SETTINGS.map((setting) => (
                          <TableCell
                            key={setting}
                            className={`${classes.headCell} ${classes.settingCell}`}
                          >
                            <Tooltip title={SETTING_HINTS[setting]}>
                              <span className={classes.headHint}>{SETTING_LABELS[setting]}</span>
                            </Tooltip>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map((p) => (
                        <TableRow
                          key={p.id}
                          hover
                          selected={checked.has(p.id)}
                          classes={{ selected: classes.rowSelected }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={checked.has(p.id)}
                              onChange={() => toggleOne(p.id)}
                              inputProps={{ 'aria-label': `Select ${getProjectDisplayName(p)}` }}
                            />
                          </TableCell>
                          <TableCell>
                            <span className={classes.projectName}>{getProjectDisplayName(p)}</span>
                            {p.isArchived ? <span className={classes.badge}>Archived</span> : null}
                            {p.isHidden ? <span className={classes.badge}>Deleted</span> : null}
                            {p.chat && !p.ingest ? (
                              <Tooltip title="The chat answers from what was ingested before; new files aren't ingested.">
                                <span className={classes.badge}>Not ingesting</span>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                          {SETTINGS.map((setting) => {
                            const reason = blockedReason(p, setting, !p[setting], entitled);
                            const toggle = (
                              <Switch
                                size="small"
                                color="primary"
                                checked={p[setting]}
                                disabled={!!reason || busy.has(p.id)}
                                onChange={(e, on) => changeSetting(p, setting, on)}
                                inputProps={{
                                  'aria-label': `${
                                    SETTING_LABELS[setting]
                                  } for ${getProjectDisplayName(p)}`,
                                }}
                              />
                            );
                            return (
                              <TableCell key={setting} className={classes.settingCell}>
                                {/* A disabled switch gets no pointer events: the tooltip goes on
                                    its wrapper so it can say why. */}
                                {reason ? (
                                  <Tooltip title={reason}>
                                    <span>{toggle}</span>
                                  </Tooltip>
                                ) : (
                                  toggle
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {!pageRows.length ? (
                    <div className={classes.empty}>
                      {projects.length ? 'No projects match.' : 'This subscriber has no projects.'}
                    </div>
                  ) : null}
                  <TablePagination
                    component="div"
                    count={filtered.length}
                    page={page}
                    onChangePage={(e, next) => setPage(next)}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onChangeRowsPerPage={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                  />
                </>
              )}
            </>
          )}
        </Paper>
      </div>

      <Dialog
        open={confirmSwitch !== null}
        onClose={() => setConfirmSwitch(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmSwitch ? 'Turn CENTERLINE AI back on?' : 'Turn CENTERLINE AI off everywhere?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={classes.dialogText}>
            {confirmSwitch
              ? 'Every subscriber with access can use it again, and the Query Tool can read their projects again, as set on this page.'
              : "Nobody can use CENTERLINE AI, site admins included, and within seconds the Query Tool can't read any project. Subscribers' access and projects' settings stay as they are, so turning it back on restores them."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSwitch(null)} color="primary">
            Cancel
          </Button>
          <Button onClick={() => changeSwitch(!!confirmSwitch)} color="primary" variant="contained">
            {confirmSwitch ? 'Turn on' : 'Turn off everywhere'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOff} onClose={() => setConfirmOff(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Turn off CENTERLINE AI access?</DialogTitle>
        <DialogContent>
          <DialogContentText className={classes.dialogText}>
            {onCount
              ? `${selected?.name}'s ${onCount} ${
                  onCount === 1 ? 'project' : 'projects'
                } with Ingestion or Chat on will have both switched off, and CENTERLINE AI stops reading them. Turning access back on doesn't switch them on again.`
              : `${selected?.name} will no longer be able to use CENTERLINE AI.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOff(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={() => changeAccess(false)} color="primary" variant="contained">
            Turn off
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmBulkIngest}
        onClose={() => setConfirmBulkIngest(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Switch Ingestion on for {bulkIngest} {bulkIngest === 1 ? 'project' : 'projects'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={classes.dialogText}>
            Within about a minute the Query Tool starts ingesting every file and email of these
            projects that it hasn&apos;t ingested yet, then each new one as it is added. Large
            projects take a while and cost compute.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkIngest(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={() => changeChecked('ingest', true)} color="primary" variant="contained">
            Switch on
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
